import { BlogPost } from '@/types/blog'
import { Button } from '@/components/ui/button'
import { MessageCircle, Star } from 'lucide-react'

const whatsappUrl =
  'https://wa.me/5511963087549?text=Ol%C3%A1%2C%20li%20o%20artigo%20sobre%20futebol%20e%20timidez%20e%20gostaria%20de%20saber%20mais%20sobre%20a%20abordagem%20de%20voc%C3%AAs%21'

export const postFutebolTimidez: BlogPost = {
  slug: 'futebol-ajuda-crianca-timida',
  title: 'Futebol Ajuda Criança Tímida? Como o Esporte Desenvolve Confiança',
  excerpt:
    'Seu filho é tímido ou retraído? Veja como o futebol, quando bem orientado, pode ser a chave para desbloquear a autoconfiança e habilidades sociais.',
  date: '16 Fev, 2026',
  author: 'Psicologia do Esporte SuperFut',
  category: 'Comportamento',
  location: 'São Paulo, SP',
  image:
    'https://img.usecurling.com/p/800/400?q=shy%20child%20playing%20soccer',
  metaDescription:
    'Descubra como o futebol desenvolve confiança em crianças tímidas. Metodologia respeitosa e dicas.',
  faq: [
    {
      question: 'O futebol não é muito agressivo para tímidos?',
      answer:
        'Pode ser, se o ambiente for de alta competição. No SuperFut, focamos em cooperação e diversão, criando um espaço seguro para o tímido.',
    },
    {
      question: 'E se ele não quiser entrar em campo?',
      answer:
        'Respeitamos o tempo da criança. Ela pode começar observando, interagir apenas com o professor e, aos poucos, se integrar ao grupo.',
    },
    {
      question: 'Aulas individuais ajudam?',
      answer:
        'Sim, a aula individual (personal) é uma excelente "ponte" para a criança ganhar confiança técnica antes de entrar em um grupo.',
    },
    {
      question: 'Como o professor lida com a timidez?',
      answer:
        'Nossos professores são treinados para usar reforço positivo, evitar exposição desnecessária e celebrar pequenas conquistas.',
    },
    {
      question: 'Vai ajudar a fazer amigos na escola?',
      answer:
        'Geralmente sim. O esporte dá um assunto em comum e habilidades de interação que são transferidas para o ambiente escolar.',
    },
    {
      question: 'Devo forçar a ir na aula?',
      answer:
        'Não recomendamos forçar. O ideal é negociar, encorajar e validar os sentimentos, mostrando que é um momento de brincadeira.',
    },
    {
      question: 'Quanto tempo demora para ver resultados?',
      answer:
        'Cada criança é única. Algumas se soltam na primeira aula, outras levam alguns meses. A constância é a chave.',
    },
    {
      question: 'O que fazer se ele chorar?',
      answer:
        'Acolher. O choro é comunicação. Entendemos o motivo e adaptamos a atividade para que ele se sinta seguro novamente.',
    },
    {
      question: 'Futebol melhora a fala?',
      answer:
        'A necessidade de pedir a bola e comemorar estimula a comunicação verbal e não-verbal.',
    },
    {
      question: 'Pais devem ficar assistindo?',
      answer:
        'No início, a presença dos pais dá segurança. Com o tempo, incentivamos que a criança fique sozinha com o professor para desenvolver autonomia.',
    },
  ],
  content: (
    <div className="space-y-8 text-slate-700 leading-relaxed">
      <div className="prose prose-slate max-w-none">
        <p className="text-xl">
          É comum pais de crianças tímidas ou introvertidas terem receio de
          colocar os filhos em esportes coletivos como o futebol. "E se ele for
          excluído?", "E se gritarem com ele?". Essas preocupações são
          legítimas. Porém, quando praticado em um ambiente pedagógico e
          acolhedor, o futebol é uma das ferramentas mais poderosas para
          desenvolver a autoconfiança.
        </p>
      </div>

      <section>
        <h2 className="text-2xl font-bold text-super-text mb-4">
          A Psicologia por trás do Jogo
        </h2>
        <p className="mb-4">
          A timidez muitas vezes vem do medo do julgamento ou da insegurança
          sobre as próprias capacidades. O futebol atua nessas duas frentes:
        </p>
        <ul className="list-disc pl-6 space-y-2 mb-6">
          <li>
            <strong>Competência:</strong> Ao aprender a dominar a bola, a
            criança percebe que é capaz de controlar algo. Isso gera um
            sentimento de autoeficácia.
          </li>
          <li>
            <strong>Pertencimento:</strong> No time, a criança tem uma função.
            Ela é necessária. Sentir-se parte de algo maior é um antídoto
            poderoso contra o isolamento.
          </li>
        </ul>
      </section>

      <section className="bg-yellow-50 p-6 rounded-2xl border border-yellow-100">
        <h2 className="text-2xl font-bold text-super-text mb-4">
          O Ambiente Importa (E Muito!)
        </h2>
        <p className="mb-4">
          Uma escolinha tradicional focada em competição pode, de fato, assustar
          uma criança tímida. Por isso, no <strong>SuperFut Kids</strong>, nossa
          abordagem é diferente:
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h3 className="font-bold text-super-text">Erro = Aprendizado</h3>
            <p className="text-sm">
              Nunca punimos o erro. Errar o chute é apenas parte do processo de
              aprender a chutar certo.
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h3 className="font-bold text-super-text">Pequenos Grupos</h3>
            <p className="text-sm">
              Nossas turmas reduzidas ou aulas individuais diminuem a pressão
              social e facilitam a conexão.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-super-text mb-4">
          Dicas para Pais de Crianças Tímidas
        </h2>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="w-8 h-8 rounded-full bg-super-blue flex items-center justify-center text-white font-bold flex-shrink-0">
              1
            </div>
            <div>
              <h3 className="font-bold text-super-text">Comece no Quintal</h3>
              <p>
                Antes da primeira aula, brinque de chutar bola com ele em casa.
                A familiaridade com o objeto reduz a ansiedade.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-8 h-8 rounded-full bg-super-blue flex items-center justify-center text-white font-bold flex-shrink-0">
              2
            </div>
            <div>
              <h3 className="font-bold text-super-text">Evite Rótulos</h3>
              <p>
                Evite dizer "ele é tímido" na frente da criança. Prefira "ele
                está observando antes de entrar". Isso valida o comportamento
                sem cristalizar a identidade.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-8 h-8 rounded-full bg-super-blue flex items-center justify-center text-white font-bold flex-shrink-0">
              3
            </div>
            <div>
              <h3 className="font-bold text-super-text">Celebre o Esforço</h3>
              <p>
                Elogie a coragem de ter entrado na quadra, não apenas o gol. A
                coragem de tentar é o que importa.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-super-blue to-cyan-500 text-white p-8 rounded-3xl text-center shadow-lg">
        <Star className="w-12 h-12 text-yellow-300 mx-auto mb-4" />
        <h2 className="text-2xl md:text-3xl font-display font-bold mb-4">
          Vamos construir essa confiança juntos?
        </h2>
        <p className="text-lg text-blue-50 mb-8 max-w-2xl mx-auto">
          Nossos professores são especialistas em acolhimento. Agende uma
          conversa e explique as necessidades do seu filho. Faremos um plano
          personalizado para ele brilhar.
        </p>
        <Button
          size="lg"
          className="bg-white text-super-blue hover:bg-slate-100 font-bold text-lg px-8 py-6 rounded-full shadow-lg hover:scale-105 transition-all w-full sm:w-auto"
          asChild
        >
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="w-6 h-6 mr-2" />
            Falar com Especialista
          </a>
        </Button>
      </section>
    </div>
  ),
}
